// src/pages/SubscriptionsOverview.js
import React, { useEffect, useState } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Paper,
  Button,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  TablePagination,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';

import NavBar from '../components/NavBar';
import theme from '../theme';
import BACKEND_URL from '../config';

const DASHBOARD_PATH = '/dashboard';
const CONTACT_PATH = '/contact';

function SubscriptionsOverviewInner() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [overview, setOverview] = useState(null);

  // Local state for "Explore other datasets" search/filter/pagination
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedIndustry, setSelectedIndustry] = useState('All'); // now stands for parent industry
  const [page, setPage] = useState(0);
  const rowsPerPage = 10;

  const navigate = useNavigate();

  // Initial load of dataset overview
  useEffect(() => {
    async function load() {
      setLoading(true);
      setError('');
      try {
        const token = localStorage.getItem('access_token');
        if (!token) {
          setError('You are not logged in. Please log in again.');
          setLoading(false);
          return;
        }

        const res = await fetch(`${BACKEND_URL}/api/me/datasets`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.error || `HTTP ${res.status}`);
        }

        const data = await res.json();
        setOverview(data);
      } catch (e) {
        console.error('Error loading dataset overview', e);
        setError(e.message || 'Failed to load dataset overview.');
      } finally {
        setLoading(false);
      }
    }

    load();
  }, []);

  // Safely derive values even before overview is loaded
  const orgName = overview?.organization?.name || 'your team';
  const selfCount = overview?.self_uploaded?.count || 0;
  const subscribedDatasets = overview?.subscribed?.datasets || [];
  const subscribedCount = overview?.subscribed?.total_interviews || 0;
  const otherDatasets = overview?.other?.datasets || [];

  const hasSubscriptions = subscribedDatasets.length > 0;
  const hasOtherDatasets = otherDatasets.length > 0;

  // Helper for "Interviews available" column in "Explore other datasets"
  const renderInterviewAvailability = (count) => {
    if (!count || count <= 0) {
      return 'On demand';
    }
    return String(count);
  };

  // Build list of *parent industry* options from all "other" datasets
  const parentIndustryOptions = (() => {
    if (!hasOtherDatasets) return [];
    const set = new Set();
    otherDatasets.forEach((ds) => {
      const parent = ds.parent_industry;
      if (parent) set.add(parent);
    });
    return Array.from(set).sort();
  })();

  // Filter "otherDatasets" by search term + selected *parent* industry
  const filteredOtherDatasets = (() => {
    let list = otherDatasets;

    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      list = list.filter((ds) =>
        (ds.label || '').toLowerCase().includes(q)
      );
    }

    if (selectedIndustry !== 'All') {
      list = list.filter((ds) => (ds.parent_industry || '') === selectedIndustry);
    }

    return list;
  })();

  // Reset page when filters change
  useEffect(() => {
    setPage(0);
  }, [searchTerm, selectedIndustry]);

  // Paginate filtered list
  const paginatedOtherDatasets = (() => {
    const start = page * rowsPerPage;
    return filteredOtherDatasets.slice(start, start + rowsPerPage);
  })();

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // Early returns AFTER all hooks are declared
  if (loading) {
    return (
      <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!overview) {
    return null;
  }

  return (
    <Box sx={{ padding: 3, maxWidth: 1200, mx: 'auto' }}>
      {/* Hero */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
          Welcome back, {orgName}!
        </Typography>
        <Typography variant="body1" sx={{ color: 'text.secondary', mb: 2 }}>
          Here’s the interview data your team can analyze today.
        </Typography>

        {/* Stats chips – total removed, only show self + subscribed */}
        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', mb: 2 }}>
          <Chip label={`${selfCount} self-uploaded interviews`} />
          <Chip label={`${subscribedCount} from subscribed datasets`} />
        </Stack>
      </Box>

      {/* Current datasets (table) */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" sx={{ mb: 2 }}>
          Your current datasets
        </Typography>

        <Paper sx={{ p: 2 }}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Dataset</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Industry</TableCell>
                  <TableCell>Parent industry</TableCell>
                  <TableCell align="right">Interviews</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {/* Self-uploaded dataset row */}
                <TableRow>
                  <TableCell>Your uploaded files</TableCell>
                  <TableCell>Self-uploaded</TableCell>
                  <TableCell>—</TableCell>
                  <TableCell>—</TableCell>
                  <TableCell align="right">
                    {selfCount}
                  </TableCell>
                </TableRow>

                {/* Subscribed datasets rows or "none" message */}
                {!hasSubscriptions ? (
                  <TableRow>
                    <TableCell colSpan={5}>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        You’re not subscribed to any shared datasets yet.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  subscribedDatasets.map((ds) => (
                    <TableRow key={ds.key}>
                      <TableCell>{ds.label}</TableCell>
                      <TableCell>
                        {ds.type === 'industry' ? 'Industry dataset' : 'Product-specific dataset'}
                      </TableCell>
                      <TableCell>
                        {ds.type === 'product' && ds.industry_label
                          ? ds.industry_label
                          : ds.type === 'industry'
                            ? ds.label
                            : '—'}
                      </TableCell>
                      <TableCell>
                        {ds.parent_industry || '—'}
                      </TableCell>
                      <TableCell align="right">
                        {ds.interview_count || 0}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Single CTA under the table */}
          <Box sx={{ mt: 2, textAlign: 'right' }}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => navigate(DASHBOARD_PATH)}
            >
              View dashboard
            </Button>
          </Box>
        </Paper>
      </Box>

      {/* Explore other datasets (table) */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" sx={{ mb: 1 }}>
          Explore other datasets
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
          These datasets are available but not yet included in your analysis. You can also
          use this section to request access to new datasets we haven’t collected yet.
        </Typography>

        <Paper sx={{ p: 2 }}>
          {hasOtherDatasets ? (
            <>
              {/* Search + Parent-industry filter pills */}
              <Box
                sx={{
                  mb: 2,
                  display: 'flex',
                  flexDirection: { xs: 'column', md: 'row' },
                  gap: 2,
                  alignItems: { xs: 'stretch', md: 'center' },
                }}
              >
                <TextField
                  size="small"
                  label="Search datasets"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  sx={{ maxWidth: 320 }}
                />
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  <Chip
                    label="All parent industries"
                    clickable
                    size="small"
                    color={selectedIndustry === 'All' ? 'primary' : 'default'}
                    onClick={() => setSelectedIndustry('All')}
                  />
                  {parentIndustryOptions.map((parent) => (
                    <Chip
                      key={parent}
                      label={parent}
                      clickable
                      size="small"
                      color={selectedIndustry === parent ? 'primary' : 'default'}
                      onClick={() => setSelectedIndustry(parent)}
                    />
                  ))}
                </Box>
              </Box>

              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Dataset</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Industry</TableCell>
                      <TableCell>Parent industry</TableCell>
                      <TableCell align="right">Interviews available</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredOtherDatasets.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5}>
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            No datasets match your search or filters.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      paginatedOtherDatasets.map((ds) => (
                        <TableRow key={ds.key}>
                          <TableCell>{ds.label}</TableCell>
                          <TableCell>
                            {ds.type === 'industry' ? 'Industry dataset' : 'Product-specific dataset'}
                          </TableCell>
                          <TableCell>
                            {ds.type === 'product' && ds.industry_label
                              ? ds.industry_label
                              : ds.type === 'industry'
                                ? ds.label
                                : '—'}
                          </TableCell>
                          <TableCell>
                            {ds.parent_industry || '—'}
                          </TableCell>
                          <TableCell align="right">
                            {renderInterviewAvailability(ds.interview_count)}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Pagination */}
              <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
                <TablePagination
                  component="div"
                  count={filteredOtherDatasets.length}
                  page={page}
                  onPageChange={handleChangePage}
                  rowsPerPage={rowsPerPage}
                  rowsPerPageOptions={[rowsPerPage]}
                  onRowsPerPageChange={() => {}}
                />
              </Box>
            </>
          ) : (
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              We don’t have any additional datasets to show right now. If you’re looking
              for something specific, get in touch and we’ll help.
            </Typography>
          )}

          {/* CTA + explanation right next to it */}
          <Box
            sx={{
              mt: 2,
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: 'space-between',
              gap: 2,
            }}
          >
            <Typography variant="body2" sx={{ color: 'text.secondary', maxWidth: 500 }}>
              Don’t see your exact industry or product listed? Click “Request access” to tell us what dataset you’d like. 
              We’ll go out and collect those interviews for you and keep the dataset refreshed every 4 months.
            </Typography>
            <Box sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
              <Button
                variant="outlined"
                onClick={() => navigate(CONTACT_PATH)}
              >
                Request access
              </Button>
            </Box>
          </Box>
        </Paper>
      </Box>

      {/* How it works */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>
          How your access works
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
          Your organization can analyze two kinds of data:
        </Typography>
        <ul style={{ marginTop: 0 }}>
          <li>
            <Typography variant="body2">
              <strong>Self-uploaded interviews</strong> – files your team uploads, only visible
              to your organization.
            </Typography>
          </li>
          <li>
            <Typography variant="body2">
              <strong>Subscribed datasets</strong> – we create curated interview datasets by industry or product, and we refresh them every 4 months.
            </Typography>
          </li>
        </ul>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          You can change which datasets are included in your analysis using the
          “Interview datasets” filter on the Dashboard and other analysis pages.
        </Typography>
      </Box>
    </Box>
  );
}

export default function SubscriptionsOverview() {
  return (
    <ThemeProvider theme={theme}>
      <NavBar />
      <SubscriptionsOverviewInner />
    </ThemeProvider>
  );
}
